import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import MideastNavigation from './MideastNavigation';

const SIDEBAR_COLLAPSED = 60;
const SIDEBAR_EXPANDED  = 224;

const MIDEAST_THEME = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&family=Bebas+Neue&display=swap');

  /* ── RESET ───────────────────────────────────────────── */
  *, *::before, *::after { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { background: #080C18; font-family: 'DM Sans', sans-serif; color: #E2E8F0; }
  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.10); border-radius: 4px; }

  /* ── ROOT LAYOUT ─────────────────────────────────────── */
  .md-root {
    display: flex;
    width: 100%;
    min-height: 100vh;
    background: #080C18;
  }

  /* ── SIDEBAR ─────────────────────────────────────────── */
  .md-sidebar {
    position: fixed;
    top: 0; left: 0; bottom: 0;
    width: ${SIDEBAR_COLLAPSED}px;
    background: #0D1120;
    border-right: 1px solid rgba(255,255,255,0.055);
    box-shadow: 4px 0 24px rgba(0,0,0,0.32);
    display: flex;
    flex-direction: column;
    z-index: 100;
    overflow: hidden;
    transition: width 0.25s cubic-bezier(0.4,0,0.2,1);
  }
  .md-sidebar:hover {
    width: ${SIDEBAR_EXPANDED}px;
  }

  /* Logo */
  .md-sidebar-logo {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 20px 13px 16px;
    flex-shrink: 0;
    overflow: hidden;
    white-space: nowrap;
  }
  .md-logo-icon {
    width: 34px; height: 34px;
    border-radius: 10px;
    background: linear-gradient(135deg, #0EA5E9, #F59E0B);
    display: flex; align-items: center; justify-content: center;
    font-family: 'Bebas Neue', sans-serif;
    font-size: 18px; color: #fff;
    flex-shrink: 0;
    box-shadow: 0 0 14px rgba(245,158,11,0.35);
  }
  .md-logo-text {
    display: flex; flex-direction: column;
    overflow: hidden;
  }
  .md-logo-name {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 20px; letter-spacing: 0.12em;
    background: linear-gradient(90deg, #0EA5E9, #F59E0B);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    line-height: 1;
  }
  .md-logo-sub {
    font-family: 'DM Mono', monospace;
    font-size: 8px; letter-spacing: 0.22em;
    color: rgba(245,158,11,0.40);
    text-transform: uppercase;
    margin-top: 2px;
  }

  /* Divider */
  .md-sidebar-divider {
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent);
    margin: 4px 12px;
    flex-shrink: 0;
  }

  /* Nav list */
  .md-nav-list {
    list-style: none;
    margin: 8px 0 0;
    padding: 0;
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
  }
  .md-nav-list li { padding: 0; }

  /* Nav item */
  .md-nav-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 9px 13px;
    margin: 2px 8px;
    border-radius: 10px;
    border: 1px solid transparent;
    color: rgba(255,255,255,0.42);
    font-family: 'DM Sans', sans-serif;
    font-size: 13px; font-weight: 500;
    text-decoration: none;
    cursor: pointer;
    background: none;
    width: calc(100% - 16px);
    transition: all 0.18s ease;
    white-space: nowrap;
    overflow: hidden;
    position: relative;
  }
  .md-nav-item:hover {
    background: rgba(255,255,255,0.055);
    color: rgba(255,255,255,0.82);
    border-color: transparent;
  }
  .md-nav-item--active {
    background: linear-gradient(135deg, rgba(14,165,233,0.16), rgba(245,158,11,0.10)) !important;
    color: #E2E8F0 !important;
    border-color: rgba(14,165,233,0.22) !important;
    font-weight: 600 !important;
  }
  .md-nav-item--active .md-nav-icon {
    color: #0EA5E9 !important;
  }
  .md-nav-item--active::before {
    content: '';
    position: absolute;
    left: -8px; top: 6px; bottom: 6px;
    width: 3px;
    border-radius: 0 3px 3px 0;
    background: linear-gradient(180deg, #0EA5E9, #F59E0B);
  }

  .md-nav-icon {
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    width: 20px;
    color: rgba(255,255,255,0.28);
    transition: color 0.18s;
  }
  .md-nav-item:hover .md-nav-icon { color: rgba(255,255,255,0.65); }

  .md-nav-label {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .md-nav-arrow {
    opacity: 0;
    color: rgba(255,255,255,0.20);
    transition: opacity 0.18s;
    flex-shrink: 0;
  }
  .md-sidebar:hover .md-nav-arrow { opacity: 1; }
  .md-nav-item--active .md-nav-arrow { color: rgba(14,165,233,0.50); }

  /* Logout button */
  .md-sidebar-bottom {
    flex-shrink: 0;
    padding-bottom: 12px;
  }
  .md-nav-logout {
    color: rgba(255,255,255,0.28) !important;
  }
  .md-nav-logout:hover {
    background: rgba(239,68,68,0.08) !important;
    color: rgba(248,113,113,0.80) !important;
    border-color: rgba(239,68,68,0.15) !important;
  }
  .md-nav-logout:hover .md-nav-icon { color: rgba(248,113,113,0.70) !important; }

  /* ── MAIN CONTENT ────────────────────────────────────── */
  .md-main {
  margin-left: ${SIDEBAR_COLLAPSED}px;
  width: calc(100% - ${SIDEBAR_COLLAPSED}px);
  min-height: 100vh;
  background: #080C18;
  overflow-x: hidden;
  overflow-y: auto;        /* ← add this */
  height: 100vh;           /* ← add this */
  transition: margin-left 0.25s cubic-bezier(0.4,0,0.2,1),
              width      0.25s cubic-bezier(0.4,0,0.2,1);
}

  /* ── TOP BAR ─────────────────────────────────────────── */
  .md-topbar {
    position: sticky; top: 0; z-index: 50;
    height: 56px;
    background: rgba(13,17,32,0.90);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid rgba(255,255,255,0.055);
    display: flex; align-items: center;
    padding: 0 28px;
    gap: 16px;
  }
  .md-topbar-title {
    flex: 1;
    font-size: 13px; font-weight: 700;
    color: rgba(255,255,255,0.30);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    font-family: 'DM Mono', monospace;
  }
  .md-topbar-title span {
    color: rgba(255,255,255,0.70);
  }
  .md-topbar-user {
    display: flex; align-items: center; gap: 10px;
  }
  .md-topbar-avatar {
    width: 30px; height: 30px;
    border-radius: 8px;
    background: linear-gradient(135deg, #0EA5E9, #F59E0B);
    display: flex; align-items: center; justify-content: center;
    font-size: 12px; font-weight: 800; color: #fff;
    flex-shrink: 0;
  }
  .md-topbar-name {
    font-size: 12px; font-weight: 600;
    color: rgba(255,255,255,0.55);
  }
  .md-topbar-badge {
    font-family: 'DM Mono', monospace;
    font-size: 8px; letter-spacing: 0.14em;
    color: rgba(245,158,11,0.55);
    background: rgba(245,158,11,0.08);
    border: 1px solid rgba(245,158,11,0.18);
    border-radius: 5px;
    padding: 2px 7px;
    text-transform: uppercase;
  }

  /* ── PAGE CONTENT ────────────────────────────────────── */
  .md-page {
    padding: 28px;
    min-height: calc(100vh - 56px);
  }

  /* ── SHARED COMPONENT STYLES ─────────────────────────── */

  /* Stat card */
  .md-stat-card {
    background: #0D1120;
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 14px;
    padding: 20px 22px;
    display: flex; flex-direction: column; gap: 6px;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  .md-stat-card:hover {
    border-color: rgba(14,165,233,0.18);
    box-shadow: 0 4px 24px rgba(0,0,0,0.25);
  }
  .md-stat-label {
    font-size: 10px; font-weight: 700;
    letter-spacing: 0.14em; text-transform: uppercase;
    color: rgba(255,255,255,0.28);
    font-family: 'DM Mono', monospace;
  }
  .md-stat-value {
    font-size: 32px; font-weight: 800;
    color: #F1F5F9; letter-spacing: -0.03em; line-height: 1;
  }
  .md-stat-sub {
    font-size: 11px; color: rgba(255,255,255,0.28);
  }

  /* Surface card */
  .md-card {
    background: #0D1120;
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 14px;
    overflow: hidden;
  }
  .md-card-header {
    padding: 18px 22px;
    border-bottom: 1px solid rgba(255,255,255,0.055);
    display: flex; align-items: center; gap: 12px;
  }
  .md-card-title {
    font-size: 14px; font-weight: 700;
    color: #F1F5F9; flex: 1;
  }
  .md-card-body { padding: 22px; }

  /* Table */
  .md-table { width: 100%; border-collapse: collapse; }
  .md-table th {
    font-size: 9px; font-weight: 700;
    letter-spacing: 0.16em; text-transform: uppercase;
    color: rgba(255,255,255,0.28);
    font-family: 'DM Mono', monospace;
    padding: 10px 16px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    text-align: left;
  }
  .md-table td {
    padding: 13px 16px;
    font-size: 13px; color: #CBD5E0;
    border-bottom: 1px solid rgba(255,255,255,0.04);
  }
  .md-table tbody tr:hover td { background: rgba(255,255,255,0.025); }
  .md-table tbody tr:last-child td { border-bottom: none; }

  /* Buttons */
  .md-btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 8px 16px;
    border-radius: 9px; border: none; cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-size: 12px; font-weight: 700;
    letter-spacing: 0.04em;
    transition: all 0.18s ease;
  }
  .md-btn-primary {
    background: linear-gradient(90deg, #0EA5E9, #F59E0B);
    color: #fff;
    box-shadow: 0 4px 14px rgba(14,165,233,0.25);
  }
  .md-btn-primary:hover {
    box-shadow: 0 6px 20px rgba(245,158,11,0.35);
    transform: translateY(-1px);
  }
  .md-btn-ghost {
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.10);
    color: rgba(255,255,255,0.60);
  }
  .md-btn-ghost:hover {
    background: rgba(255,255,255,0.08);
    color: rgba(255,255,255,0.85);
  }
  .md-btn-danger {
    background: rgba(239,68,68,0.12);
    border: 1px solid rgba(239,68,68,0.22);
    color: rgba(248,113,113,0.85);
  }
  .md-btn-danger:hover {
    background: rgba(239,68,68,0.20);
    color: #FCA5A5;
  }

  /* Input */
  .md-input {
    width: 100%;
    padding: 10px 14px;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.09);
    border-radius: 9px;
    color: #E2E8F0;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  .md-input:focus {
    border-color: rgba(14,165,233,0.40);
    box-shadow: 0 0 0 3px rgba(14,165,233,0.08);
  }
  .md-input::placeholder { color: rgba(255,255,255,0.20); }
  .md-label {
    display: block;
    font-size: 10px; font-weight: 700;
    letter-spacing: 0.12em; text-transform: uppercase;
    color: rgba(255,255,255,0.35);
    margin-bottom: 7px;
    font-family: 'DM Mono', monospace;
  }
  .md-field { margin-bottom: 18px; }

  /* Badges */
  .md-badge {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 3px 9px; border-radius: 20px;
    font-size: 10px; font-weight: 700;
    letter-spacing: 0.06em;
    font-family: 'DM Mono', monospace;
  }
  .md-badge-blue   { background: rgba(14,165,233,0.12); color: #38BDF8; border: 1px solid rgba(14,165,233,0.20); }
  .md-badge-gold   { background: rgba(245,158,11,0.12); color: #FCD34D; border: 1px solid rgba(245,158,11,0.20); }
  .md-badge-red    { background: rgba(239,68,68,0.12);  color: #FCA5A5; border: 1px solid rgba(239,68,68,0.20); }
  .md-badge-green  { background: rgba(34,197,94,0.12);  color: #86EFAC; border: 1px solid rgba(34,197,94,0.20); }
  .md-badge-grey   { background: rgba(255,255,255,0.06); color: rgba(255,255,255,0.40); border: 1px solid rgba(255,255,255,0.10); }

  /* Modal */
  .md-modal-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.65);
    backdrop-filter: blur(6px);
    z-index: 200;
    display: flex; align-items: center; justify-content: center;
    animation: fadeIn 0.15s ease;
  }
  .md-modal {
    background: #0D1120;
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 20px;
    width: 100%; max-width: 520px;
    max-height: 88vh;
    overflow-y: auto;
    box-shadow: 0 24px 80px rgba(0,0,0,0.55);
    animation: fadeUp 0.2s ease;
  }
  .md-modal-header {
    padding: 22px 24px 18px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    display: flex; align-items: center; gap: 12px;
  }
  .md-modal-title {
    font-size: 16px; font-weight: 800;
    color: #F1F5F9; flex: 1;
  }
  .md-modal-body { padding: 24px; }
  .md-modal-footer {
    padding: 16px 24px;
    border-top: 1px solid rgba(255,255,255,0.06);
    display: flex; justify-content: flex-end; gap: 10px;
  }
  .md-modal-close {
    background: none; border: none; cursor: pointer;
    color: rgba(255,255,255,0.30); padding: 4px;
    border-radius: 6px; transition: color 0.15s;
    display: flex; align-items: center;
  }
  .md-modal-close:hover { color: rgba(255,255,255,0.70); }

  /* Grid helpers */
  .md-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .md-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; }
  .md-grid-4 { display: grid; grid-template-columns: repeat(4,1fr); gap: 16px; }

  /* Empty state */
  .md-empty {
    text-align: center; padding: 60px 20px;
    color: rgba(255,255,255,0.20);
    font-size: 13px;
  }
  .md-empty-icon { font-size: 32px; margin-bottom: 12px; opacity: 0.30; }

  /* Alert */
  .md-alert {
    padding: 12px 16px; border-radius: 10px;
    font-size: 12px; font-weight: 600;
    display: flex; align-items: center; gap: 8px;
    margin-bottom: 16px;
  }
  .md-alert-error { background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.20); color: #FCA5A5; }
  .md-alert-success { background: rgba(34,197,94,0.08); border: 1px solid rgba(34,197,94,0.20); color: #86EFAC; }

  @keyframes fadeIn { from{opacity:0} to{opacity:1} }
  @keyframes fadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
  @keyframes spin   { to{transform:rotate(360deg)} }
  .md-spinner {
    display: inline-block; width: 14px; height: 14px;
    border: 2px solid rgba(255,255,255,0.20);
    border-top-color: #0EA5E9;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }
`;

const MideastAdminLayout = () => {
  const navigate  = useNavigate();
  const mainRef   = useRef(null);
  const [user, setUser] = useState(null);

  // Auth guard
  useEffect(() => {
    const token = localStorage.getItem('mideast_token');
    const userData = localStorage.getItem('mideast_user');

    if (!token) {
      navigate('/mideast-login');
      return;
    }

    if (userData) {
      const parsed = JSON.parse(userData);
      if (parsed.type !== 'mideast-admin') {
        navigate('/mideast-login');
        return;
      }
      setUser(parsed);
    }
  }, [navigate]);

  const getInitials = useCallback((u) => {
    if (!u) return 'A';
    return `${u.firstname?.[0] || ''}${u.lastname?.[0] || ''}`.toUpperCase() || 'A';
  }, []);

  if (!user) return null;

  return (
    <>
      <style>{MIDEAST_THEME}</style>

      <div className="md-root">
        {/* Sidebar */}
        <MideastNavigation />

        {/* Main */}
        <div className="md-main" ref={mainRef}>

          {/* Top bar */}
          <div className="md-topbar">
            <div className="md-topbar-title">
              NOVAA / <span>Mideast Admin</span>
            </div>
            <div className="md-topbar-user">
              <span className="md-topbar-badge">Mideast Admin</span>
              <span className="md-topbar-name">
                {user.firstname} {user.lastname}
              </span>
              <div className="md-topbar-avatar">
                {getInitials(user)}
              </div>
            </div>
          </div>

          {/* Page content rendered by child routes */}
          <div className="md-page">
            <Outlet />
          </div>

        </div>
      </div>
    </>
  );
};

export default MideastAdminLayout;